import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Phone, FileText } from 'lucide-react';
import Home from './pages/Home';
import RequestForm from './pages/RequestForm';
import WhatsAppButton from './components/WhatsAppButton';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Toaster } from './components/ui/sonner';

// Lazy load pages that are not immediately needed
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const TechnicianPanel = lazy(() => import('./pages/TechnicianPanel'));
const KombiArizaKodlari = lazy(() => import('./pages/KombiArizaKodlari'));
const KlimaArizaKodlari = lazy(() => import('./pages/KlimaArizaKodlari'));
const LocationService = lazy(() => import('./pages/LocationService'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Loading component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '18px',
    color: '#1976d2'
  }}>
    Yükleniyor...
  </div>
);

function AppContent() {
  const location = useLocation();
  const hideWhatsApp = location.pathname === '/giris' || location.pathname === '/admin' || location.pathname === '/teknisyen';

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/servisler" element={<Services />} />
          <Route path="/servis/:serviceSlug" element={<ServiceDetail />} />
          <Route path="/talep" element={<RequestForm />} />
          <Route path="/hakkimizda" element={<About />} />
          <Route path="/iletisim" element={<Contact />} />
          <Route path="/kombi-ariza-kodlari" element={<KombiArizaKodlari />} />
          <Route path="/klima-ariza-kodlari" element={<KlimaArizaKodlari />} />
          <Route path="/demirdokum-ariza-kodlari" element={<KombiArizaKodlari />} />
          {/* Location-based SEO pages - explicit routes for SEO */}
          <Route path="/kusadasi-kombi-servisi" element={<LocationService />} />
          <Route path="/kusadasi-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/kusadasi-klima-servisi" element={<LocationService />} />
          <Route path="/soke-kombi-servisi" element={<LocationService />} />
          <Route path="/soke-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/soke-klima-servisi" element={<LocationService />} />
          <Route path="/aydin-kombi-servisi" element={<LocationService />} />
          <Route path="/aydin-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/aydin-klima-servisi" element={<LocationService />} />
          <Route path="/guzelcamli-kombi-servisi" element={<LocationService />} />
          <Route path="/guzelcamli-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/guzelcamli-klima-servisi" element={<LocationService />} />
          <Route path="/davutlar-kombi-servisi" element={<LocationService />} />
          <Route path="/davutlar-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/davutlar-klima-servisi" element={<LocationService />} />
          <Route path="/incirliova-kombi-servisi" element={<LocationService />} />
          <Route path="/incirliova-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/incirliova-klima-servisi" element={<LocationService />} />
          <Route path="/germencik-kombi-servisi" element={<LocationService />} />
          <Route path="/germencik-beyaz-esya-servisi" element={<LocationService />} />
          <Route path="/germencik-klima-servisi" element={<LocationService />} />
          {/* Specific service pages for better SEO */}
          <Route path="/aydin-klima-montaji" element={<LocationService />} />
          <Route path="/kusadasi-klima-montaji" element={<LocationService />} />
          <Route path="/soke-klima-montaji" element={<LocationService />} />
          <Route path="/aydin-kombi-bakimi" element={<LocationService />} />
          <Route path="/kusadasi-kombi-bakimi" element={<LocationService />} />
          <Route path="/soke-kombi-bakimi" element={<LocationService />} />
          {/* Blog pages */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Legal pages */}
          <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
          <Route path="/kullanim-kosullari" element={<TermsOfService />} />
          <Route path="/giris" element={<Login />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/teknisyen" element={<TechnicianPanel />} />
        </Routes>
      </Suspense>
      {!hideWhatsApp && <WhatsAppButton />}
      
      {/* Mobile Sticky CTA Bar */}
      {!hideWhatsApp && (
        <div className="mobile-sticky-cta">
          <div className="cta-content">
            <a href="tel:+905518478521" className="btn-call-now">
              <Phone size={20} />
              Hemen Ara
            </a>
            <Link to="/talep" className="btn-request-now">
              <FileText size={20} />
              Online Talep
            </Link>
          </div>
        </div>
      )}
      
      <PWAInstallPrompt />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;